import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BusinessRuleType, EntityDefinition } from '../types/entity-definition.interface';
import { GenericEntity } from '../types/generic-entity.interface';
import { StoreInterfaceService } from './store-interface.service';

@Injectable({
  providedIn: 'root',
})
export class BusinessRulesService {
  private readonly storeService = inject(StoreInterfaceService);
  private readonly translate = inject(TranslocoService);

  canDelete<T extends GenericEntity>(entityDefinition: EntityDefinition<T>, entity: T): boolean {
    if (!entityDefinition.validationRules) {
      return true;
    }

    const deleteRules = entityDefinition.validationRules.filter(rule => rule.type === BusinessRuleType.DELETE_VALIDATION);

    if (deleteRules.length === 0) {
      return true;
    }

    return deleteRules.every(rule => rule.rule(entity, this.storeService));
  }

  canCreate<T extends GenericEntity>(entityDefinition: EntityDefinition<T>, entity: Partial<T>): boolean {
    if (!entityDefinition.validationRules) {
      return true;
    }

    const createRules = entityDefinition.validationRules.filter(rule => rule.type === BusinessRuleType.CREATE_VALIDATION);

    if (createRules.length === 0) {
      return true;
    }

    return createRules.every(rule => rule.rule(entity as T, this.storeService));
  }

  canEdit<T extends GenericEntity>(entityDefinition: EntityDefinition<T>, entity: T): boolean {
    if (!entityDefinition.validationRules) {
      return true;
    }

    const editRules = entityDefinition.validationRules.filter(rule => rule.type === BusinessRuleType.EDIT_VALIDATION);

    if (editRules.length === 0) {
      return true;
    }

    return editRules.every(rule => rule.rule(entity, this.storeService));
  }

  getDeleteValidationMessages<T extends GenericEntity>(entityDefinition: EntityDefinition<T>, entity: T): string[] {
    if (!entityDefinition.validationRules) {
      return [];
    }

    const deleteRules = entityDefinition.validationRules.filter(rule => rule.type === BusinessRuleType.DELETE_VALIDATION);

    return deleteRules
      .filter(rule => !rule.rule(entity, this.storeService))
      .map(rule => (rule.errorMessageKey ? this.translate.translate(rule.errorMessageKey) : rule.errorMessage))
      .filter((msg): msg is string => !!msg);
  }

  getCreateValidationMessages<T extends GenericEntity>(entityDefinition: EntityDefinition<T>, entity: Partial<T>): string[] {
    if (!entityDefinition.validationRules) {
      return [];
    }

    const createRules = entityDefinition.validationRules.filter(rule => rule.type === BusinessRuleType.CREATE_VALIDATION);

    return createRules
      .filter(rule => !rule.rule(entity as T, this.storeService))
      .map(rule => (rule.errorMessageKey ? this.translate.translate(rule.errorMessageKey) : rule.errorMessage))
      .filter((msg): msg is string => !!msg);
  }

  getEditValidationMessages<T extends GenericEntity>(entityDefinition: EntityDefinition<T>, entity: T): string[] {
    if (!entityDefinition.validationRules) {
      return [];
    }

    const editRules = entityDefinition.validationRules.filter(rule => rule.type === BusinessRuleType.EDIT_VALIDATION);

    return editRules
      .filter(rule => !rule.rule(entity, this.storeService))
      .map(rule => (rule.errorMessageKey ? this.translate.translate(rule.errorMessageKey) : rule.errorMessage))
      .filter((msg): msg is string => !!msg);
  }
}
